import { brand } from "../../config/theme";
import { buildCorridorRing } from "../../utils/geofenceCorridor";
import React, { useEffect, useMemo } from "react";
import { Map, Marker, useMap } from "@vis.gl/react-google-maps";
import type { MapMouseEvent } from "@vis.gl/react-google-maps";

// Definir la interfaz para las props del componente
interface MapPickerProps {
  lat: number;
  lng: number;
  radius: number;
  shape?: string;
  type?: string;
  onLocationSelect?: (lat: number, lng: number) => void;
  onRadiusChange?: (radius: number) => void;
  coordinates?: { lat: number; lng: number }[];
  onPointsChange?: (coordinates: { lat: number; lng: number }[]) => void;
  onRouteCalculated?: (distance: number) => void; // Para geocercas lineales
  estimatedTime?: string; // Tiempo estimado para rutas lineales
  polygonArea?: number; // Área para geocercas poligonales
  routeDistance?: number; // Distancia para geocercas lineales
  isEdit?: boolean; // Para saber si estamos en modo edición
  firstPointRadius?: number; // Radio del círculo en el primer punto (rutas)
  lastPointRadius?: number; // Radio del círculo en el último punto (rutas)
  onFirstPointRadiusChange?: (radius: number) => void; // Para actualizar radio del primer punto
  onLastPointRadiusChange?: (radius: number) => void; // Para actualizar radio del último punto
  minHeight?: number;
  corridorBuffer?: number;
  onAxisCalculated?: (path: google.maps.LatLngLiteral[]) => void;
  storedAxis?: google.maps.LatLngLiteral[];
}

// Definir el tipo para la posición (alias para claridad)
type Position = google.maps.LatLngLiteral;

// Variable global del módulo para almacenar la última ruta procesada y evitar bucles
let lastProcessedRouteKey: string | null = null;


const TYPE_COLORS: Record<string, { stroke: string; fill: string }> = {
  warehouse: { stroke: "#2563eb", fill: "#93c5fd" }, // azul
  restrictedArea: { stroke: "#ef4444", fill: "#fca5a5" }, // rojo
  customer: { stroke: "#0891b2", fill: "#a5f3fc" }, // cyan
  route: { stroke: "#a21caf", fill: "#e9d5ff" }, // morado
  maintenance: { stroke: "#f59e42", fill: "#fde68a" }, // naranja/ámbar
  authorizedStop: { stroke: brand.primary, fill: brand.primary + "40" }, // primary with 25% opacity
  default: { stroke: "#6b7280", fill: "#d1d5db" }, // gris
};

const colorSetFor = (type: string) => TYPE_COLORS[type] || TYPE_COLORS.default;

// Función para resetear la ruta procesada (usada al cerrar modal)
export const resetLastProcessedCoordinates = () => {
  lastProcessedRouteKey = null;
};

const buildContainerStyle = (minHeight: number): React.CSSProperties => ({
  width: "100%",
  height: "100%",
  minHeight: `${minHeight}px`,
  borderRadius: "0.5rem",
});

// Calcular coordenadas de los puntos extremos del círculo (norte, sur, este, oeste)
const getRadiusControlPoints = (
  center: Position,
  radius: number
): Record<string, Position> => {
  const latOffset = radius / 111000; // 1 grado de latitud ≈ 111 km
  const lngOffset = radius / (111000 * Math.cos((center.lat * Math.PI) / 180));

  return {
    north: { lat: center.lat + latOffset, lng: center.lng },
    south: { lat: center.lat - latOffset, lng: center.lng },
    east: { lat: center.lat, lng: center.lng + lngOffset },
    west: { lat: center.lat, lng: center.lng - lngOffset },
  };
};

// Calcular la distancia (en metros) entre dos puntos en el mapa
const getDistanceInMeters = (p1: Position, p2: Position): number => {
  const R = 6371000; // Radio de la Tierra en metros
  const dLat = ((p2.lat - p1.lat) * Math.PI) / 180;
  const dLng = ((p2.lng - p1.lng) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((p1.lat * Math.PI) / 180) *
      Math.cos((p2.lat * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Calcular la distancia entre dos puntos en píxeles en la pantalla
const getPixelDistance = (p1: Position, p2: Position, map: google.maps.Map): number => {
  const projection = map.getProjection();
  if (!projection) return Infinity;

  const point1 = projection.fromLatLngToPoint(new google.maps.LatLng(p1.lat, p1.lng));
  const point2 = projection.fromLatLngToPoint(new google.maps.LatLng(p2.lat, p2.lng));

  if (!point1 || !point2) return Infinity;

  const dx = point1.x - point2.x;
  const dy = point1.y - point2.y;
  return Math.sqrt(dx * dx + dy * dy);
};

// Convertir grados a metros aproximadamente
const latLngToMeters = (lat: number, lng: number): { x: number; y: number } => {
  const earthRadius = 6371000; // metros
  const x = (lng * Math.PI / 180) * earthRadius * Math.cos(lat * Math.PI / 180);
  const y = (lat * Math.PI / 180) * earthRadius;
  return { x, y };
};

// Encontrar el punto más cercano en un segmento de línea (en metros)
const getClosestPointOnSegment = (
  mousePos: Position,
  p1: Position,
  p2: Position,
  map: google.maps.Map
): { point: Position; distance: number } => {
  // Convertir a metros para cálculos de distancia
  const m1 = latLngToMeters(p1.lat, p1.lng);
  const m2 = latLngToMeters(p2.lat, p2.lng);
  const mouse = latLngToMeters(mousePos.lat, mousePos.lng);

  // Vector from p1 to p2
  const dx = m2.x - m1.x;
  const dy = m2.y - m1.y;
  const lenSq = dx * dx + dy * dy;

  if (lenSq === 0) {
    const distance = Math.sqrt((mouse.x - m1.x) ** 2 + (mouse.y - m1.y) ** 2);
    return { point: p1, distance };
  }

  // Parameter t for closest point
  let t = ((mouse.x - m1.x) * dx + (mouse.y - m1.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));

  const closestX = m1.x + t * dx;
  const closestY = m1.y + t * dy;

  // Distancia en metros
  const distance = Math.sqrt(
    (mouse.x - closestX) ** 2 + (mouse.y - closestY) ** 2
  );

  // Interpolar lat/lng basado en t
  const closestLat = p1.lat + t * (p2.lat - p1.lat);
  const closestLng = p1.lng + t * (p2.lng - p1.lng);

  return {
    point: { lat: closestLat, lng: closestLng },
    distance,
  };
};

// Crear ícono de símbolo de Google Maps
const createSymbolIcon = (
  color: string,
  scale: number = 8,
): google.maps.Symbol | undefined => {
  try {
    if (typeof google !== "undefined" && google?.maps?.SymbolPath) {
      return {
        path: google.maps.SymbolPath.CIRCLE,
        scale,
        fillColor: color,
        fillOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 2,
      };
    }
  } catch (e) {
    // Fallback silencioso
  }

  // Este fallback nunca debería verse si google maps está correctamente cargado
  // Pero si ocurre, retorna un objeto válido
  return undefined;
};

const MapShape = ({
  position,
  radius,
  shape,
  type = "default",
  onLocationSelect,
  coordinates,
  onPointsChange,
  onRouteCalculated,
  isEdit = false,
  firstPointRadius = 0,
  lastPointRadius = 0,
  corridorBuffer = 0,
  onAxisCalculated,
  storedAxis,
  onAxisResolved,
}: {
  position: Position;
  radius: number;
  shape: string;
  type?: string;
  onLocationSelect?: (lat: number, lng: number) => void;
  coordinates?: { lat: number; lng: number }[];
  onPointsChange?: (coordinates: { lat: number; lng: number }[]) => void;
  onRouteCalculated?: (distance: number) => void;
  isEdit?: boolean;
  firstPointRadius?: number;
  lastPointRadius?: number;
  corridorBuffer?: number;
  onAxisCalculated?: (path: google.maps.LatLngLiteral[]) => void;
  storedAxis?: google.maps.LatLngLiteral[];
  onAxisResolved?: (path: google.maps.LatLngLiteral[]) => void;
}) => {
  const map = useMap();

  const framedShapeRef = React.useRef<string | null>(null);
  const panDebounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const cancelPan = () => {
    if (!panDebounceRef.current) return;
    clearTimeout(panDebounceRef.current);
    panDebounceRef.current = null;
  };

  const frameBounds = (
    bounds: google.maps.LatLngBounds | null | undefined,
    target: Position,
  ) => {
    if (!map || !bounds) return;

    if (framedShapeRef.current !== shape) {
      cancelPan();
      map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
      framedShapeRef.current = shape;
      return;
    }

    if (map.getBounds()?.contains(target) ?? false) return;

    cancelPan();
    panDebounceRef.current = setTimeout(() => {
      panDebounceRef.current = null;
      map.panTo(target);
    }, 100);
  };

  const circleRef = React.useRef<google.maps.Circle | null>(null);
  const rectangleRef = React.useRef<google.maps.Rectangle | null>(null);
  const polygonRef = React.useRef<google.maps.Polygon | null>(null);
  const firstPointCircleRef = React.useRef<google.maps.Circle | null>(null);
  const lastPointCircleRef = React.useRef<google.maps.Circle | null>(null);
  const corridorRef = React.useRef<google.maps.Polygon | null>(null);
  const axisLineRef = React.useRef<google.maps.Polyline | null>(null);
  const [routeAxis, setRouteAxis] = React.useState<google.maps.LatLngLiteral[]>(
    [],
  );
  const axisCallbackRef = React.useRef(onAxisCalculated);
  const axisResolvedRef = React.useRef(onAxisResolved);

  const hasStoredAxis = Array.isArray(storedAxis) && storedAxis.length >= 2;

  React.useEffect(() => {
    axisCallbackRef.current = onAxisCalculated;
  }, [onAxisCalculated]);

  React.useEffect(() => {
    axisResolvedRef.current = onAxisResolved;
  }, [onAxisResolved]);

  React.useEffect(() => {
    axisResolvedRef.current?.(routeAxis);
  }, [routeAxis]);

  // Refs para Directions API (rutas lineales)
  const directionsServiceRef =
    React.useRef<google.maps.DirectionsService | null>(null);
  const directionsRendererRef =
    React.useRef<google.maps.DirectionsRenderer | null>(null);

  // Memorizar opciones del renderer para evitar recreaciones
  const rendererOptions = useMemo(() => {
    const colorSet = colorSetFor(type);

    return {
      draggable: true,
      panel: null, // No mostrar panel de instrucciones
      suppressMarkers: true, // ← Ocultar markers por defecto de Directions API
      polylineOptions: {
        strokeColor: colorSet.stroke,
        strokeOpacity: 0.8,
        strokeWeight: 4,
      },
    };
  }, [type]);

  // Dependencia extendida: también depende de los valores de los puntos
  useEffect(() => {
    if (!map) return;
    if (circleRef.current) {
      circleRef.current.setMap(null);
      circleRef.current = null;
    }
    if (rectangleRef.current) {
      rectangleRef.current.setMap(null);
      rectangleRef.current = null;
    }
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }

    const colorSet = colorSetFor(type);

    const options = {
      strokeColor: colorSet.stroke,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: colorSet.fill,
      fillOpacity: 0.35,
      map: map,
    };

    if (shape === "circular") {
      const newCircle = new google.maps.Circle({
        ...options,
        center: position,
        radius: radius,
        draggable: false, // No hacer el círculo draggable, solo el Marker
        editable: false,
        clickable: false, // Evitar que el círculo capture clicks
      });
      // No agregar listener de center_changed en el círculo - el Marker maneja el drag

      frameBounds(newCircle.getBounds(), position);

      circleRef.current = newCircle;
    } else if (shape === "rectangle") {
      const latOffset = radius / 111000;
      const lngOffset =
        radius / (111000 * Math.cos((position.lat * Math.PI) / 180));
      const bounds = {
        north: position.lat + latOffset,
        south: position.lat - latOffset,
        east: position.lng + lngOffset,
        west: position.lng - lngOffset,
      };
      const newRectangle = new google.maps.Rectangle({
        ...options,
        bounds: bounds,
      });
      rectangleRef.current = newRectangle;
    } else if (
      shape === "polygonal" &&
      coordinates &&
      coordinates.length >= 3
    ) {
      const newPolygon = new google.maps.Polygon({
        ...options,
        paths: coordinates,
      });

      const bounds = new google.maps.LatLngBounds();
      coordinates.forEach((coord) => {
        bounds.extend(coord);
      });
      frameBounds(bounds, bounds.getCenter().toJSON());

      polygonRef.current = newPolygon;
    } else if (shape === "linear" && hasStoredAxis) {
      const axis = storedAxis as google.maps.LatLngLiteral[];
      const colorSet = colorSetFor(type);

      if (directionsRendererRef.current) {
        directionsRendererRef.current.setMap(null);
        directionsRendererRef.current = null;
      }

      axisLineRef.current = new google.maps.Polyline({
        path: axis,
        strokeColor: colorSet.stroke,
        strokeOpacity: 0.8,
        strokeWeight: 4,
        clickable: false,
        map,
      });

      setRouteAxis(axis);

      const bounds = new google.maps.LatLngBounds();
      axis.forEach((point) => bounds.extend(point));
      frameBounds(bounds, bounds.getCenter().toJSON());
    } else if (shape === "linear" && coordinates && coordinates.length >= 2) {
      // Protección adicional: ignorar coordenadas por defecto SOLO en modo edición
      const isDefaultCoordinates =
        isEdit && // ← Solo aplicar en modo edición
        coordinates.length === 2 &&
        coordinates[0].lat === 19.4326 &&
        coordinates[0].lng === -99.1332 &&
        coordinates[1].lat === 19.44 &&
        coordinates[1].lng === -99.14;

      if (isDefaultCoordinates) {
        return;
      }

      const routeKey = JSON.stringify({ type, coordinates });
      const shouldDrawRoute =
        lastProcessedRouteKey !== routeKey || !directionsRendererRef.current;

      if (!shouldDrawRoute) {
        return;
      }

      lastProcessedRouteKey = routeKey;
      setRouteAxis((prev) => (prev.length === 0 ? prev : []));

      // Crear el servicio de direcciones si no existe
      if (!directionsServiceRef.current) {
        directionsServiceRef.current = new google.maps.DirectionsService();
      }

      // Siempre recrear el renderer para actualizar colores cuando el tipo cambia
      if (directionsRendererRef.current) {
        directionsRendererRef.current.setMap(null); // Limpiar renderer anterior
      }

      directionsRendererRef.current = new google.maps.DirectionsRenderer(
        rendererOptions
      );
      directionsRendererRef.current.setMap(map);

      // Preparar waypoints (puntos intermedios)
      const waypoints = coordinates.slice(1, -1).map((coord) => ({
        location: coord,
        stopover: true,
      }));

      // Crear la solicitud de ruta
      const request = {
        origin: coordinates[0],
        destination: coordinates[coordinates.length - 1],
        waypoints: waypoints,
        travelMode: google.maps.TravelMode.DRIVING,
      };

      // Ejecutar la solicitud de ruta
      directionsServiceRef.current.route(request, (result, status) => {
        if (status === google.maps.DirectionsStatus.OK && result) {
          directionsRendererRef.current?.setDirections(result);

          // Calcular distancia total
          let totalDistance = 0;
          if (result.routes && result.routes[0] && result.routes[0].legs) {
            result.routes[0].legs.forEach((leg) => {
              totalDistance += leg.distance?.value || 0;
            });
            const distanceInKm = totalDistance / 1000;
            onRouteCalculated?.(distanceInKm);
          }

          const overview = result.routes?.[0]?.overview_path || [];
          const axis = overview.map((p) => ({ lat: p.lat(), lng: p.lng() }));
          if (axis.length >= 2) {
            axisCallbackRef.current?.(axis);
            setRouteAxis(axis);
          }
        }
      });
    } else if (shape === "corridor") {
      const newCircle = new google.maps.Circle({
        ...options,
        center: position,
        radius: radius,
      });
      circleRef.current = newCircle;
    }

    // Cleanup en el return
    return () => {
      cancelPan();

      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
      if (rectangleRef.current) {
        rectangleRef.current.setMap(null);
        rectangleRef.current = null;
      }
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
        polygonRef.current = null;
      }
      if (firstPointCircleRef.current) {
        firstPointCircleRef.current.setMap(null);
        firstPointCircleRef.current = null;
      }
      if (lastPointCircleRef.current) {
        lastPointCircleRef.current.setMap(null);
        lastPointCircleRef.current = null;
      }
      if (axisLineRef.current) {
        axisLineRef.current.setMap(null);
        axisLineRef.current = null;
      }
      // NO limpiar directionsRenderer aquí para evitar perder la ruta
      // Solo limpiar si realmente estamos cambiando de forma
      if (directionsRendererRef.current && shape !== "linear") {
        directionsRendererRef.current.setMap(null);
        directionsRendererRef.current = null;
      }
    };
    // Ahora depende de los valores de los puntos
  }, [
    map,
    position.lat,
    position.lng,
    radius,
    shape,
    type,
    JSON.stringify(coordinates),
    firstPointRadius,
    lastPointRadius,
    hasStoredAxis,
    JSON.stringify(storedAxis),
    // Eliminamos onPointsChange y onRouteCalculated para evitar bucles infinitos
  ]);

  useEffect(() => {
    if (!map) return;

    const clearCorridor = () => {
      if (!corridorRef.current) return;
      corridorRef.current.setMap(null);
      corridorRef.current = null;
    };

    clearCorridor();

    if (shape !== "linear" || corridorBuffer <= 0 || routeAxis.length < 2)
      return;

    const colorSet = colorSetFor(type);

    corridorRef.current = new google.maps.Polygon({
      paths: buildCorridorRing(routeAxis, corridorBuffer),
      strokeColor: colorSet.stroke,
      strokeOpacity: 0.7,
      strokeWeight: 1,
      fillColor: colorSet.fill,
      fillOpacity: 0.25,
      clickable: false,
      map,
    });

    return clearCorridor;
  }, [map, shape, type, corridorBuffer, routeAxis]);

  useEffect(() => {
    if (!map) return;

    const clearPointCircles = () => {
      if (firstPointCircleRef.current) {
        firstPointCircleRef.current.setMap(null);
        firstPointCircleRef.current = null;
      }
      if (lastPointCircleRef.current) {
        lastPointCircleRef.current.setMap(null);
        lastPointCircleRef.current = null;
      }
    };

    clearPointCircles();

    if (shape !== "linear" || !coordinates || coordinates.length < 1) return;

    const isDefaultCoordinates =
      isEdit &&
      coordinates.length === 2 &&
      coordinates[0].lat === 19.4326 &&
      coordinates[0].lng === -99.1332 &&
      coordinates[1].lat === 19.44 &&
      coordinates[1].lng === -99.14;

    if (isDefaultCoordinates) return;

    const anchors =
      routeAxis.length >= 2
        ? { start: routeAxis[0], end: routeAxis[routeAxis.length - 1] }
        : { start: coordinates[0], end: coordinates[coordinates.length - 1] };

    if (firstPointRadius > 0) {
      firstPointCircleRef.current = new google.maps.Circle({
        strokeColor: "#10b981",
        strokeOpacity: 0.9,
        strokeWeight: 2,
        fillColor: "#10b981",
        fillOpacity: 0.12,
        clickable: false,
        map,
        center: anchors.start,
        radius: firstPointRadius,
      });
    }

    if (lastPointRadius > 0 && coordinates.length >= 2) {
      lastPointCircleRef.current = new google.maps.Circle({
        strokeColor: "#ef4444",
        strokeOpacity: 0.9,
        strokeWeight: 2,
        fillColor: "#ef4444",
        fillOpacity: 0.12,
        clickable: false,
        map,
        center: anchors.end,
        radius: lastPointRadius,
      });
    }

    return clearPointCircles;
  }, [
    map,
    shape,
    isEdit,
    JSON.stringify(coordinates),
    firstPointRadius,
    lastPointRadius,
    routeAxis,
  ]);

  return null;
};

const GhostCircleRenderer = ({
  draggingRadius,
}: {
  draggingRadius: { center: Position; radius: number } | null;
}) => {
  const map = useMap();
  const circleRef = React.useRef<google.maps.Circle | null>(null);

  // Initialize circle once when map loads
  useEffect(() => {
    if (!map) return;

    circleRef.current = new google.maps.Circle({
      center: { lat: 0, lng: 0 },
      radius: 0,
      map: map,
      strokeColor: "#3b82f6",
      strokeOpacity: 0.4,
      strokeWeight: 2,
      fillColor: "#3b82f6",
      fillOpacity: 0.1,
      visible: false,
    });

    return () => {
      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
    };
  }, [map]);

  // Update circle properties when dragging
  useEffect(() => {
    if (!circleRef.current) return;

    if (!draggingRadius) {
      circleRef.current.setVisible(false);
      return;
    }

    circleRef.current.setCenter(draggingRadius.center);
    circleRef.current.setRadius(draggingRadius.radius);
    circleRef.current.setVisible(true);
  }, [draggingRadius]);

  return null;
};

// MapCenterSyncronizer disabled - was causing map pan glitches
// The defaultCenter prop in Map component handles initial centering
// Users can pan/zoom manually without automatic interference

const MapPicker: React.FC<MapPickerProps> = ({
  lat,
  lng,
  radius,
  shape = "circular",
  type = "default",
  coordinates,
  onLocationSelect,
  onRadiusChange,
  onPointsChange,
  onRouteCalculated,
  estimatedTime,
  polygonArea,
  routeDistance,
  isEdit = false,
  firstPointRadius = 0,
  lastPointRadius = 0,
  onFirstPointRadiusChange,
  onLastPointRadiusChange,
  minHeight = 400,
  corridorBuffer = 0,
  onAxisCalculated,
  storedAxis,
}) => {
  const [draggingRadius, setDraggingRadius] = React.useState<{
    center: Position;
    radius: number;
  } | null>(null);

  // El eje resuelto (Directions o el almacenado) vive aqui porque los circulos
  // de zona y sus puntos de control tienen que anclarse al mismo punto que
  // evalua la ingesta: el extremo del eje, no el waypoint del usuario.
  const [routeAxis, setRouteAxis] = React.useState<Position[]>([]);

  // Estado para rastrear el punto donde se puede insertar un nuevo punto en un edge del polígono
  const [hoverPointOnEdge, setHoverPointOnEdge] = React.useState<{
    point: Position;
    edgeIndex: number;
  } | null>(null);

  const zoneAnchors = React.useMemo(() => {
    if (routeAxis.length >= 2) {
      return { start: routeAxis[0], end: routeAxis[routeAxis.length - 1] };
    }
    if (coordinates && coordinates.length >= 2) {
      return { start: coordinates[0], end: coordinates[coordinates.length - 1] };
    }
    return null;
  }, [routeAxis, coordinates]);

  // Memoize the marker position to prevent unnecessary re-renders
  const markerPosition = React.useMemo(() => ({ lat, lng }), [lat, lng]);
  const mapCenter = React.useMemo(() => ({ lat, lng }), [lat, lng]);

  // Memoize icon creation to ensure they're created when google is available
  const centerIcon = React.useMemo(
    () => createSymbolIcon("#3b82f6", 8),
    []
  );
  const radiusControlIcon = React.useMemo(
    () => createSymbolIcon("#10b981", 6),
    []
  );
  const firstPointRadiusIcon = React.useMemo(
    () => createSymbolIcon("#10b981", 5),
    []
  );
  const lastPointRadiusIcon = React.useMemo(
    () => createSymbolIcon("#ef4444", 5),
    []
  );

  // Track if marker is currently dragging to prevent pan events from affecting marker
  const isDraggingRef = React.useRef(false);
  const dragEndTimeRef = React.useRef<number>(0);

  // Manejar mousemove para detectar si el mouse está sobre un edge del polígono
  const handleMapMouseMove = (event: MapMouseEvent) => {
    if (shape !== "polygonal" || !coordinates || coordinates.length < 3 || !onPointsChange) {
      setHoverPointOnEdge(null);
      return;
    }

    const mousePos = event.detail.latLng;
    if (!mousePos) {
      setHoverPointOnEdge(null);
      return;
    }

    const map = event.map;
    if (!map) {
      setHoverPointOnEdge(null);
      return;
    }

    // Calcular umbral dinámico basado en zoom: a mayor zoom, menor distancia permitida
    const zoom = map.getZoom() || 15;
    // A zoom 15: ~100 metros, zoom 18: ~12 metros, zoom 12: ~400 metros
    const HOVER_THRESHOLD = Math.max(40, 1024 / Math.pow(2, zoom - 8)); // metros

    let closestEdge = null;
    let minDistance = HOVER_THRESHOLD;

    // Revisar cada edge del polígono
    for (let i = 0; i < coordinates.length; i++) {
      const p1 = coordinates[i];
      const p2 = coordinates[(i + 1) % coordinates.length]; // Edge que cierra el polígono

      const { point, distance } = getClosestPointOnSegment(mousePos, p1, p2, map);

      if (distance < minDistance) {
        minDistance = distance;
        closestEdge = { point, edgeIndex: i };
      }
    }

    setHoverPointOnEdge(closestEdge);
  };

  // Manejar clic en el mapa - ignore clicks during/after drag
  const handleMapClick = (event: MapMouseEvent) => {
    // Para polígonos, no permitir clicks en el mapa (solo clicks en el marcador +)
    if (shape === "polygonal") {
      return;
    }

    if (!event.detail.latLng) return;

    // Ignore clicks shortly after drag ends (within 300ms) to prevent map pan from updating position
    const timeSinceDragEnd = Date.now() - dragEndTimeRef.current;
    if (timeSinceDragEnd < 300 || isDraggingRef.current) return;

    const newLat = event.detail.latLng.lat;
    const newLng = event.detail.latLng.lng;
    if (onLocationSelect) {
      onLocationSelect(newLat, newLng);
    }
  };

  // Manejar drag del marcador
  const handleMarkerDragStart = () => {
    isDraggingRef.current = true;
  };

  const handleMarkerDragEnd = (e: any) => {
    isDraggingRef.current = false;
    dragEndTimeRef.current = Date.now();

    const newLat = e.latLng?.lat();
    const newLng = e.latLng?.lng();
    if (newLat !== undefined && newLng !== undefined && onLocationSelect) {
      onLocationSelect(newLat, newLng);
    }
  };

  return (
    <div
      style={buildContainerStyle(minHeight)}
      className="border-2 border-gray-300 relative rounded-2xl"
    >
      <Map
        defaultZoom={15}
        defaultCenter={mapCenter}
        gestureHandling={"greedy"}
        onClick={shape === "circular" ? undefined : handleMapClick}
        onMousemove={shape === "polygonal" ? handleMapMouseMove : undefined}
        mapId="DEMO_MAP_ID"
      >
        <GhostCircleRenderer draggingRadius={draggingRadius} />
        {shape === "polygonal" &&
          coordinates?.map((point, index) => {
            // Colores por tipo (igual que en MapShape)
            const typeColors = {
              warehouse: { stroke: "#2563eb", fill: "#93c5fd" },
              restrictedArea: { stroke: "#ef4444", fill: "#fca5a5" },
              customer: { stroke: "#0891b2", fill: "#a5f3fc" },
              route: { stroke: "#a21caf", fill: "#e9d5ff" },
              maintenance: { stroke: "#f59e42", fill: "#fde68a" },
              authorizedStop: { stroke: brand.primary, fill: brand.primary + "40" },
              default: { stroke: "#6b7280", fill: "#d1d5db" },
            };
            const colorSet =
              typeColors[type as keyof typeof typeColors] || typeColors.default;
            return (
              <Marker
                key={`point-${index}`}
                position={point}
                draggable={!!onPointsChange}
                onDragEnd={(e) => {
                  const newLat = e.latLng?.lat();
                  const newLng = e.latLng?.lng();
                  if (
                    typeof newLat === "number" &&
                    typeof newLng === "number" &&
                    onPointsChange
                  ) {
                    const updated = coordinates.map((p, i) =>
                      i === index ? { lat: newLat, lng: newLng } : p
                    );
                    onPointsChange(updated);
                  }
                }}
                icon={
                  typeof google !== "undefined" && google?.maps?.SymbolPath
                    ? {
                        path: google.maps.SymbolPath.CIRCLE,
                        scale: 8,
                        fillColor: colorSet.stroke,
                        fillOpacity: 1,
                        strokeWeight: 0,
                      }
                    : undefined
                }
              />
            );
          })}

        {/* Marcador de inserción de punto para polígonos */}
        {shape === "polygonal" && hoverPointOnEdge && (
          <Marker
            position={hoverPointOnEdge.point}
            title="Click para agregar punto"
            onClick={() => {
              if (onPointsChange && coordinates) {
                const newPoints = [
                  ...coordinates.slice(0, hoverPointOnEdge.edgeIndex + 1),
                  hoverPointOnEdge.point,
                  ...coordinates.slice(hoverPointOnEdge.edgeIndex + 1),
                ];
                onPointsChange(newPoints);
                setHoverPointOnEdge(null);
              }
            }}
            icon={
              typeof google !== "undefined" && google?.maps?.SymbolPath
                ? {
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: 6,
                    fillColor: "#10b981",
                    fillOpacity: 0.8,
                    strokeColor: "#ffffff",
                    strokeWeight: 2,
                  }
                : undefined
            }
            label={{
              text: "+",
              color: "white",
              fontSize: "14px",
              fontWeight: "bold",
            }}
          />
        )}

        {shape === "linear" &&
          coordinates?.map((point, index) => {
            const total = coordinates.length;

            // Semantic role colors for trip routes
            const isStart = index === 0;
            const isEnd = index === total - 1;
            const markerColor = isStart
              ? "#10b981" // emerald-500 — origin
              : isEnd
              ? "#ef4444" // red-500 — destination
              : "#f59e0b"; // amber-400 — waypoint

            // Larger scale for origin/destination to emphasize trip semantics
            const markerScale = isStart || isEnd ? 10 : 7;

            return (
              <Marker
                key={`linear-point-${index}`}
                position={point}
                draggable={!!onPointsChange}
                onDragEnd={(e) => {
                  const newLat = e.latLng?.lat();
                  const newLng = e.latLng?.lng();
                  if (
                    typeof newLat === "number" &&
                    typeof newLng === "number" &&
                    onPointsChange
                  ) {
                    const updated = coordinates.map((p, i) =>
                      i === index ? { lat: newLat, lng: newLng } : p
                    );
                    onPointsChange(updated);
                  }
                }}
                icon={
                  typeof google !== "undefined" && google?.maps?.SymbolPath
                    ? {
                        path: google.maps.SymbolPath.CIRCLE,
                        scale: markerScale,
                        fillColor: markerColor,
                        fillOpacity: 1,
                        strokeColor: "#ffffff",
                        strokeWeight: 2,
                      }
                    : undefined
                }
                label={{
                  text: String(index + 1),
                  color: "white",
                  fontSize: "11px",
                  fontWeight: "bold",
                }}
              />
            );
          })}

        {/* Puntos de control para radios de rutas */}
        {shape === "linear" && zoneAnchors && (
          <>
            {/* Control points for first point radius */}
            {firstPointRadius > 0 && (
              (() => {
                const firstPoint = zoneAnchors.start;
                const controlPoints = getRadiusControlPoints(firstPoint, firstPointRadius);
                return (
                  <>
                    {(["north", "south", "east", "west"] as const).map((direction) => (
                      <Marker
                        key={`first-radius-control-${direction}`}
                        position={controlPoints[direction]}
                        draggable={!!onFirstPointRadiusChange}
                        onDrag={(e) => {
                          if (!onFirstPointRadiusChange || !e.latLng) return;
                          const newLat = e.latLng.lat();
                          const newLng = e.latLng.lng();
                          const newRadius = Math.round(
                            getDistanceInMeters(firstPoint, { lat: newLat, lng: newLng })
                          );
                          setDraggingRadius({ center: firstPoint, radius: newRadius });
                        }}
                        onDragEnd={(e) => {
                          if (!onFirstPointRadiusChange || !e.latLng) return;
                          const newLat = e.latLng.lat();
                          const newLng = e.latLng.lng();
                          const newRadius = Math.round(
                            getDistanceInMeters(firstPoint, { lat: newLat, lng: newLng })
                          );
                          onFirstPointRadiusChange(newRadius);
                          setDraggingRadius(null);
                        }}
                        icon={firstPointRadiusIcon}
                      />
                    ))}
                  </>
                );
              })()
            )}

            {/* Control points for last point radius */}
            {lastPointRadius > 0 && (
              (() => {
                const lastPoint = zoneAnchors.end;
                const controlPoints = getRadiusControlPoints(lastPoint, lastPointRadius);
                return (
                  <>
                    {(["north", "south", "east", "west"] as const).map((direction) => (
                      <Marker
                        key={`last-radius-control-${direction}`}
                        position={controlPoints[direction]}
                        draggable={!!onLastPointRadiusChange}
                        onDrag={(e) => {
                          if (!onLastPointRadiusChange || !e.latLng) return;
                          const newLat = e.latLng.lat();
                          const newLng = e.latLng.lng();
                          const newRadius = Math.round(
                            getDistanceInMeters(lastPoint, { lat: newLat, lng: newLng })
                          );
                          setDraggingRadius({ center: lastPoint, radius: newRadius });
                        }}
                        onDragEnd={(e) => {
                          if (!onLastPointRadiusChange || !e.latLng) return;
                          const newLat = e.latLng.lat();
                          const newLng = e.latLng.lng();
                          const newRadius = Math.round(
                            getDistanceInMeters(lastPoint, { lat: newLat, lng: newLng })
                          );
                          onLastPointRadiusChange(newRadius);
                          setDraggingRadius(null);
                        }}
                        icon={lastPointRadiusIcon}
                      />
                    ))}
                  </>
                );
              })()
            )}
          </>
        )}

        {shape === "circular" && (
          <>
            {/* Centro del círculo */}
            <Marker
              position={markerPosition}
              draggable={!!onLocationSelect}
              onDragStart={handleMarkerDragStart}
              onDragEnd={handleMarkerDragEnd}
              icon={centerIcon}
            />

            {/* Puntos de control en los extremos para ajustar el radio */}
            {(() => {
              const controlPoints = getRadiusControlPoints({ lat, lng }, radius);
              return (
                <>
                  {(["north", "south", "east", "west"] as const).map((direction) => (
                    <Marker
                      key={`radius-control-${direction}`}
                      position={controlPoints[direction]}
                      draggable={!!onRadiusChange}
                      onDrag={(e) => {
                        if (!onRadiusChange || !e.latLng) return;
                        const newLat = e.latLng.lat();
                        const newLng = e.latLng.lng();
                        const newRadius = Math.round(
                          getDistanceInMeters({ lat, lng }, { lat: newLat, lng: newLng })
                        );
                        setDraggingRadius({ center: { lat, lng }, radius: newRadius });
                      }}
                      onDragEnd={(e) => {
                        if (!onRadiusChange || !e.latLng) return;
                        const newLat = e.latLng.lat();
                        const newLng = e.latLng.lng();
                        const newRadius = Math.round(
                          getDistanceInMeters({ lat, lng }, { lat: newLat, lng: newLng })
                        );
                        onRadiusChange(newRadius);
                        setDraggingRadius(null);
                      }}
                      icon={radiusControlIcon}
                    />
                  ))}
                </>
              );
            })()}
          </>
        )}

        <MapShape
          position={{ lat, lng }}
          radius={radius}
          shape={shape}
          type={type}
          onLocationSelect={onLocationSelect}
          coordinates={coordinates}
          onPointsChange={onPointsChange}
          onRouteCalculated={onRouteCalculated}
          isEdit={isEdit}
          firstPointRadius={firstPointRadius}
          lastPointRadius={lastPointRadius}
          corridorBuffer={corridorBuffer}
          onAxisCalculated={onAxisCalculated}
          storedAxis={storedAxis}
          onAxisResolved={setRouteAxis}
        />
      </Map>

      {/* Recuadro blanco con la info en la esquina inferior izquierda */}
      {shape === "circular" && (
        <div
          style={{
            position: "absolute",
            left: 16,
            bottom: 16,
            background: "white",
            borderRadius: 8,
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            padding: "12px 20px",
            minWidth: 220,
            zIndex: 10,
          }}
          className="border border-gray-300"
        >
          <div className="font-semibold text-gray-800 text-sm mb-1">
            Centro:{" "}
            <span className="font-normal text-xs text-gray-700">
              {Number.isFinite(lat) && Number.isFinite(lng)
                ? `${lat.toFixed(4)}, ${lng.toFixed(4)}`
                : "-"}
            </span>
          </div>
          <div className="font-semibold text-gray-800 text-sm mb-1">
            Radio:{" "}
            <span className="font-normal text-xs text-gray-700">
              {radius} m
            </span>
          </div>
        </div>
      )}

      {/* Recuadro blanco con info para forma lineal */}
      {shape === "linear" && coordinates && coordinates.length >= 2 && (
        <div
          style={{
            position: "absolute",
            left: 16,
            bottom: 16,
            background: "white",
            borderRadius: 8,
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            padding: "12px 20px",
            minWidth: 220,
            zIndex: 10,
          }}
          className="border border-gray-300"
        >
          <div className="font-semibold text-gray-800 text-sm mb-1">
            Total de puntos:{" "}
            <span className="font-normal text-xs text-gray-700">
              {coordinates.length}
            </span>
          </div>
          <div className="font-semibold text-gray-800 text-sm mb-1">
            Distancia estimada:{" "}
            <span className="font-normal text-xs text-gray-700">
              {(routeDistance || 0) > 0
                ? `${(routeDistance || 0).toFixed(1)} km`
                : "Calculando..."}
            </span>
          </div>
          <div className="font-semibold text-gray-800 text-sm mb-1">
            Tiempo estimado:{" "}
            <span className="font-normal text-xs text-gray-700">
              {estimatedTime || "Calculando..."}
            </span>
          </div>
          {corridorBuffer > 0 && (
            <div className="font-semibold text-gray-800 text-sm">
              Corredor:{" "}
              <span className="font-normal text-xs text-gray-700">
                {corridorBuffer * 2} m de ancho ({corridorBuffer} m a cada lado)
              </span>
            </div>
          )}
        </div>
      )}

      {/* Recuadro blanco con info para forma poligonal */}
      {shape === "polygonal" && coordinates && coordinates.length >= 3 && (
        <div
          style={{
            position: "absolute",
            left: 16,
            bottom: 16,
            background: "white",
            borderRadius: 8,
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            padding: "12px 20px",
            minWidth: 220,
            zIndex: 10,
          }}
          className="border border-gray-300"
        >
          <div className="font-semibold text-gray-800 text-sm mb-1">
            Total de puntos:{" "}
            <span className="font-normal text-xs text-gray-700">
              {coordinates.length}
            </span>
          </div>
          <div className="font-semibold text-gray-800 text-sm mb-1">
            Área:{" "}
            <span className="font-normal text-xs text-gray-700">
              {polygonArea && polygonArea > 0
                ? `${polygonArea.toLocaleString("es-ES", {
                    maximumFractionDigits: 0,
                  })} m²`
                : "Calculando..."}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapPicker;
